import { PushError } from "~/features/push-notifications/PushError";
import type { NativePushSubscription } from "~/features/push-notifications/schemas";
import { useSharedPushUtils } from "~/features/push-notifications/use-shared-push-utils";
import {
  fetchPushSubscription,
  getPushManager,
} from "~/features/push-notifications/utils";

import { datadogRum } from "@datadog/browser-rum";
import { useMutation } from "@tanstack/react-query";

/**
 * Hook for subscribing to push notifications.
 *
 * @returns - A mutation function that can be called to subscribe the provided employee.
 */
export const useSubscribe = () => {
  const { invalidate, vapidPublicKey } = useSharedPushUtils();

  return useMutation<
    void,
    PushError | Error,
    { employeeId: string; deviceToken?: string }
  >({
    async mutationFn({ employeeId, deviceToken }) {
      let sub: PushSubscription | NativePushSubscription | null | undefined;

      if (deviceToken) {
        sub = { registrationToken: deviceToken };
      } else {
        if (typeof Notification === "undefined") {
          return;
        }

        // 1. Ask for permission from the user
        let perm = Notification.permission;

        if (perm === "default") {
          perm = await Notification.requestPermission();
        }

        if (perm === "denied") {
          throw new PushError("user-denied", employeeId);
        }

        // 2. Subscribe to the PushManager, i.e. store the sub in the browser
        const pushManager = await getPushManager();
        sub = await pushManager?.getSubscription();
        if (!sub) {
          sub = await pushManager?.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidPublicKey,
          });
        }
      }

      if (!sub) {
        throw new PushError("subscribe-failed-browser", employeeId);
      }

      // 3. Store the sub in our database
      const res = await fetchPushSubscription(
        { subscription: sub, employeeId },
        "POST",
      );

      if (!res.ok) {
        throw new PushError("subscribe-failed-database", employeeId);
      }

      datadogRum.addAction("push-subscribed");

      // 4. Ensure state is re-fetched
      return await invalidate();
    },
    retry(count, error) {
      if (error instanceof PushError && error.code === "user-denied") {
        // Doesn't make sense to retry, it will never succeed.
        return false;
      }

      return count < 3;
    },
    async onError(error) {
      if (error instanceof PushError) {
        datadogRum.addError("push-" + error.code);
        switch (error.code) {
          case "user-denied":
          case "subscribe-failed-browser":
            // Not much we can do, just invalidate to update the state.
            return await invalidate();
        }
      } else {
        datadogRum.addError(error);
      }
    },
  });
};
